import React, { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal, Table } from '@components/common';
import FileTypeSelectionForm from '@components/common/FileTypeSelectionForm';

import {
	useMutateDashboardAssetSummaryPerOrganizationsExport,
} from '@hooks/dashboardPO/useMutateDashboardAssetSummaryPerOrganizationsExport';
import useQueryDashboardAssetSummaryPerOrganization
	from '@hooks/dashboardPO/useQueryDashboardAssetSummaryPerOrganization';

import {
	ISortBy,
	ITablePagination,
	ITableSearch, ITableSort,
} from '@interfaces/components/table';
import { IExportFileDTO } from '@interfaces/platform/dto/platformDashboardDTO';

import { defaultMeta } from '@enums/common';

import { exportDashboardPORadioButtonsData } from '@constants/common';
import { MODAL_WIDTH_455 } from '@constants/modals';

import { getColumns } from './utils';

const DashboardPOPerOrganizationTable = () => {
	const navigate = useNavigate();

	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const {
		dashboardAssetSummaryPerOrganization,
		countOfRecords,
		isLoadingDashboardAssetSummaryPerOrganization,
	} = useQueryDashboardAssetSummaryPerOrganization({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	});

	const { exportDashboardAssetSummaryPerOrganizations } = useMutateDashboardAssetSummaryPerOrganizationsExport();

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by Organisation name',
	}], [searchValue, searchHandler]);

	const handleExportDashboardPerOrganizations = useCallback(({ fileType }: IExportFileDTO) => {
		if (!fileType) return;

		exportDashboardAssetSummaryPerOrganizations({ fileType });
	}, [exportDashboardAssetSummaryPerOrganizations]);

	const columns = useMemo(() => getColumns({ navigate }), [navigate]);

	const openFileTypeSelectionModal = useCallback(() => setIsOpenFileTypeSelectionModal(true), []);
	const closeFileTypeSelectionModal = useCallback(() => setIsOpenFileTypeSelectionModal(false), []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	return (
		<>
			<Table
				columns={columns}
				data={dashboardAssetSummaryPerOrganization}
				handleCustomExport={openFileTypeSelectionModal}
				isExport
				isLoading={isLoadingDashboardAssetSummaryPerOrganization}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={closeFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionForm
					closeModals={closeFileTypeSelectionModal}
					exportRadioButtonsData={exportDashboardPORadioButtonsData}
					handlePreparingExport={handleExportDashboardPerOrganizations}
					toggleModal={closeFileTypeSelectionModal}
				/>
			</Modal>
		</>
	);
};

export default memo(DashboardPOPerOrganizationTable);
